import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { trackEvent } from "~/helpers/tracking";
import TooltipArrowBottom from "./TooltipArrowBottom";
import { TooltipStyled } from "./TooltipStyled";
import type { TooltipProps } from "./types";

const TOOLTIP_OFFSET = 20;

const getArrowOverlapStyle = (placement: string): React.CSSProperties | undefined => {
  const [side] = placement.split("-");
  if (side === "top") return { marginTop: -1 };
  if (side === "left") return { marginLeft: -1 };
  if (side === "right") return { marginLeft: 1 };
  return undefined;
};

const Tooltip: FC<TooltipProps> = ({
  children,
  onClick,
  skipRewards,
  triggerEvent = true,
  disabled = false,
  open: controlledOpen,
  onRequestClose,
  position = "top",
  interactive = false,
  tabIndex,
  html,
  title,
  theme = "light",
  className,
  style,
  arrow: showArrow = true,
  delay,
  hideDelay,
  popperOptions,
}) => {
  const [tooltipTracked, setTooltipTracked] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const arrowRef = useRef(null);

  const isOpen = controlledOpen ?? uncontrolledOpen;

  const handleOpenChange = (nextOpen: boolean) => {
    if (controlledOpen === undefined) {
      setUncontrolledOpen(nextOpen);
    }
    if (!nextOpen) {
      onRequestClose?.();
    }
  };

  const shiftPadding = popperOptions?.modifiers?.preventOverflow?.padding ?? 0;

  const middleware = useMemo(
    () => [
      offset(TOOLTIP_OFFSET),
      flip(),
      shift({ padding: shiftPadding }),
      ...(showArrow ? [arrow({ element: arrowRef })] : []),
    ],
    [shiftPadding, showArrow]
  );

  const { refs, floatingStyles, context } = useFloating({
    placement: position,
    open: disabled ? false : isOpen,
    onOpenChange: handleOpenChange,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  const hoverDelay =
    delay !== undefined || hideDelay !== undefined ? { open: delay ?? 0, close: hideDelay ?? 0 } : undefined;

  const hover = useHover(context, {
    move: false,
    enabled: !disabled,
    delay: hoverDelay,
    handleClose: interactive ? safePolygon() : undefined,
  });
  const focus = useFocus(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const tooltipText = html?.props?.children?.props?.children || html?.props?.children;

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleOnClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onClick?.();
  };

  const trackTooltipEvent = () => {
    if (!tooltipTracked && triggerEvent !== false) {
      setTooltipTracked(true);
      trackEvent({
        action: "toolTip",
        data: {
          detail: {
            text: tooltipText,
          },
        },
      });
    }
  };

  const themeClass =
    theme === "dark"
      ? "fuel-tooltip--dark"
      : theme === "transparent"
      ? "fuel-tooltip--transparent"
      : "fuel-tooltip--light";
  const floatingClassName = ["fuel-tooltip", themeClass, className].filter(Boolean).join(" ");

  const content = html ?? (title ? <span>{title}</span> : null);
  const arrowOverlapStyle = useMemo(() => getArrowOverlapStyle(context.placement), [context.placement]);
  const isBottomPlacement = context.placement.startsWith("bottom");

  return (
    <TooltipStyled onClick={handleOnClick} skipRewards={skipRewards} onMouseEnter={trackTooltipEvent}>
      <span className="tooltip-button" tabIndex={tabIndex} ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </span>
      {mounted && isOpen && !disabled && content && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={floatingClassName}
            style={{ ...floatingStyles, zIndex: 9999 }}
            data-placement={context.placement}
            data-interactive={interactive ? "" : undefined}
            {...getFloatingProps()}
          >
            <div className="fuel-tooltip__content" style={style}>
              {content}
            </div>
            {showArrow &&
              (isBottomPlacement ? (
                <TooltipArrowBottom ref={arrowRef} context={context} />
              ) : (
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  className="fuel-tooltip__arrow"
                  height={11}
                  width={22}
                  fill="var(--fuel-tooltip-arrow-fill, #fff)"
                  stroke="var(--fuel-tooltip-arrow-stroke, currentColor)"
                  strokeWidth={1}
                  style={arrowOverlapStyle}
                />
              ))}
          </div>
        </FloatingPortal>
      )}
    </TooltipStyled>
  );
};

Tooltip.displayName = "Tooltip";

export default Tooltip;
export type { TooltipProps } from "./types";
